import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { CheckCircle2, Circle, MinusCircle, ChevronRight, X, Rocket } from "lucide-react";
import { API } from "../../App";

const authCfg = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

export const SetupGuideWidget = ({ status, onOpen, onRefresh }) => {
  if (!status || !status.is_owner || status.percent >= 100) return null;

  const hide = async () => {
    await axios.post(`${API}/onboarding/wizard`, { action: "widget_hide" }, authCfg()).catch(() => {});
    onRefresh();
  };
  const show = async () => {
    await axios.post(`${API}/onboarding/wizard`, { action: "widget_show" }, authCfg()).catch(() => {});
    onRefresh();
  };

  const trial = status.trial || {};

  if (status.wizard?.widget_hidden) {
    return (
      <button
        onClick={show}
        className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-full px-3 py-1 transition-colors"
        data-testid="setup-guide-reopen"
      >
        <Rocket className="w-3 h-3 text-[#FFCC00]" /> Setup Guide · {status.percent}%
      </button>
    );
  }

  const visible = (status.steps || []).slice(0, 8);
  const remaining = (status.steps || []).length - visible.length;

  return (
    <Card className="glass-card border-[#FFCC00]/30 mb-4" data-testid="setup-guide-widget">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Rocket className="w-4 h-4 text-[#FFCC00]" />
              <p className="text-sm font-semibold text-foreground font-['Manrope']">Finish Setting Up Fieldz AI</p>
              <span className="text-xs font-bold text-foreground tabular-nums" data-testid="setup-widget-percent">{status.percent}% Complete</span>
              {trial.active && (
                <Badge variant="outline" className="border-[#FFCC00]/40 text-[#b8930a] dark:text-[#FFCC00] bg-[#FFCC00]/10 text-[10px]" data-testid="setup-widget-trial">
                  Trial · {trial.days_remaining} day{trial.days_remaining === 1 ? "" : "s"} left
                </Badge>
              )}
            </div>
            <Progress value={status.percent} className="h-1.5 mt-2 w-56 max-w-full" />
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
              {visible.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`setup-widget-step-${s.id}`}>
                  {s.status === "complete"
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    : s.skip_mode === "dont_need"
                      ? <MinusCircle className="w-3 h-3" />
                      : <Circle className="w-3 h-3" />}
                  {s.title}
                </span>
              ))}
              {remaining > 0 && <span className="text-[11px] text-muted-foreground">+{remaining} more</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" onClick={onOpen} className="bg-[#FFCC00] hover:bg-[#e6b800] text-black font-semibold" data-testid="setup-widget-continue">
              Continue Setup <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" onClick={hide} title="Minimize" data-testid="setup-widget-hide">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SetupGuideWidget;
